"use client"

import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import {
  Users,
  Plus,
  Eye,
  Search,
  RefreshCw,
  ChevronRight,
  AlertTriangle,
  Clock,
  CheckCircle,
  Store,
  BarChart3,
  BookOpen,
  UserPlus,
  Phone,
  Mail,
  User,
  Building2,
  Pencil,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import {
  fetchLedgerSummary,
  fetchCustomers,
  createCustomer,
  updateCustomer,
  fetchVendorsList,
  fetchVendorSummary,
} from "../../../services/ledgerService"

const RISK_COLORS = {
  CLEAR: "bg-green-100 text-green-700 border border-green-200",
  SAFE: "bg-green-100 text-green-700 border border-green-200",
  "DUE SOON": "bg-yellow-100 text-yellow-700 border border-yellow-200",
  PENDING: "bg-gray-100 text-gray-600 border border-gray-200",
  OVERDUE: "bg-red-100 text-red-700 border border-red-200",
  "NO TRANSACTION": "bg-gray-100 text-gray-500 border border-gray-200",
}

export default function CustomerLedger() {
  const navigate = useNavigate()

  // B2C state
  const [summary, setSummary] = useState([])
  const [filtered, setFiltered] = useState([])
  const [overallOutstanding, setOverallOutstanding] = useState(0)
  const [totalCollected, setTotalCollected] = useState(0)
  const [totalBilled, setTotalBilled] = useState(0)
  const [stats, setStats] = useState({ total_customers: 0, customers_with_due: 0, overdue_customers: 0 })
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")

  // B2B state
  const [vendors, setVendors] = useState([])
  const [vendorsLoading, setVendorsLoading] = useState(false)
  const [vendorSearch, setVendorSearch] = useState("")
  const [filteredVendors, setFilteredVendors] = useState([])
  const [vendorSummary, setVendorSummary] = useState({ total_vendors: 0, total_billed: 0, total_paid: 0, total_outstanding: 0 })

  // Ledger tab view filter
  const [viewFilter, setViewFilter] = useState("b2c")

  // Edit party dialog
  const [editingParty, setEditingParty] = useState(null)
  const [editPartyLoading, setEditPartyLoading] = useState(false)

  // Add party dialog
  const [showAddParty, setShowAddParty] = useState(false)
  const [newParty, setNewParty] = useState({ name: "", phone: "", email: "" })
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const [customersRes, ledgerRes] = await Promise.all([
        fetchCustomers({ limit: 500 }),
        fetchLedgerSummary(),
      ])

      const customersList = customersRes.data || []
      const ledgerData = ledgerRes.data || []
      const ledgerStats = ledgerRes.stats || { total_customers: 0, customers_with_due: 0, overdue_customers: 0 }

      const ledgerMap = new Map()
      ledgerData.forEach((item) => {
        ledgerMap.set(item.customer_id?.toString() || item._id?.toString(), item)
      })

      const merged = customersList.map((customer) => {
        const ledger = ledgerMap.get(customer._id?.toString())
        if (ledger) {
          return { ...ledger, customer_name: customer.name, customer_phone: customer.phone, customer_email: customer.email }
        }
        return {
          _id: customer._id,
          customer_id: customer._id,
          customer_name: customer.name,
          customer_phone: customer.phone,
          customer_email: customer.email,
          total_amount: 0, total_paid: 0, total_due: 0, total_transactions: 0, last_transaction: null, risk: null,
        }
      })

      const billed = merged.reduce((s, r) => s + (r.total_amount || 0), 0)
      const collected = merged.reduce((s, r) => s + (r.total_paid || 0), 0)

      setSummary(merged)
      setFiltered(merged)
      setOverallOutstanding(ledgerRes.overall_outstanding || 0)
      setTotalBilled(billed)
      setTotalCollected(collected)
      setStats({ total_customers: customersList.length, customers_with_due: ledgerStats.customers_with_due, overdue_customers: ledgerStats.overdue_customers })
    } catch {
      toast.error("Failed to load customers")
    } finally {
      setLoading(false)
    }
  }, [])

  const loadVendors = useCallback(async () => {
    try {
      setVendorsLoading(true)
      const [listRes, summaryRes] = await Promise.all([
        fetchVendorsList({ limit: 500 }),
        fetchVendorSummary(),
      ])
      const list = listRes.data || []
      setVendors(list)
      setFilteredVendors(list)
      setVendorSummary(summaryRes.data || { total_vendors: 0, total_billed: 0, total_paid: 0, total_outstanding: 0 })
    } catch {
      toast.error("Failed to load vendors")
    } finally {
      setVendorsLoading(false)
    }
  }, [])

  useEffect(() => { load(); loadVendors() }, [load, loadVendors])

  useEffect(() => {
    if (!search.trim()) { setFiltered(summary); return }
    const q = search.toLowerCase()
    setFiltered(summary.filter((s) =>
      s.customer_name?.toLowerCase().includes(q) ||
      s.customer_phone?.toLowerCase().includes(q) ||
      s.customer_email?.toLowerCase().includes(q)
    ))
  }, [search, summary])

  useEffect(() => {
    if (!vendorSearch.trim()) { setFilteredVendors(vendors); return }
    const q = vendorSearch.toLowerCase()
    setFilteredVendors(vendors.filter(v =>
      v.name?.toLowerCase().includes(q) ||
      v.phone?.includes(q)
    ))
  }, [vendorSearch, vendors])

  const getCustomerStatus = (row) => {
    if (row.total_transactions === 0) return { label: "NO TRANSACTION", color: "gray", icon: Store }
    if (row.total_due <= 0) return { label: "CLEAR", color: "green", icon: CheckCircle }
    if (!row.risk) return { label: "PENDING", color: "gray", icon: Clock }
    const { overdue_count, due_soon_count } = row.risk
    if (overdue_count > 0) return { label: "OVERDUE", color: "red", icon: AlertTriangle }
    if (due_soon_count > 0) return { label: "DUE SOON", color: "yellow", icon: Clock }
    return { label: "SAFE", color: "green", icon: CheckCircle }
  }

  const handleAddParty = async () => {
    if (!newParty.name.trim()) { toast.error("Party name is required"); return }
    // Validate phone format (optional) and client-side duplicate phone check for quick feedback
    const phoneRaw = (newParty.phone || "").trim()
    const phoneNormalized = phoneRaw.replace(/[^+0-9]/g, "")
    if (phoneRaw) {
      const digits = phoneNormalized.replace(/[^0-9]/g, "")
      if (digits.length < 7 || digits.length > 15) {
        toast.error("Phone number must have between 7 and 15 digits")
        return
      }
      if (summary.some(c => ((c.customer_phone || "").replace(/[^0-9]/g, "") === digits))) {
        toast.error("Phone number already in use by another party")
        return
      }
      newParty.phone = phoneNormalized
    }
    try {
      setSaving(true)
      await createCustomer(newParty)
      toast.success("Party added successfully")
      setNewParty({ name: "", phone: "", email: "" })
      setShowAddParty(false)
      load()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add party")
    } finally {
      setSaving(false)
    }
  }

  const handleEditParty = async () => {
    if (!editingParty?._id || !editingParty.name?.trim()) { toast.error("Name is required"); return }
    try {
      setEditPartyLoading(true)
      await updateCustomer(editingParty._id, { name: editingParty.name, phone: editingParty.phone, email: editingParty.email })
      toast.success("Party updated")
      setEditingParty(null)
      load()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update party")
    } finally {
      setEditPartyLoading(false)
    }
  }

  const collectionRate = totalBilled > 0 ? Math.round((totalCollected / totalBilled) * 100) : 0
  const clearCustomers = summary.filter(r => r.total_due <= 0 && r.total_transactions > 0).length
  const overdueCustomers = stats.overdue_customers
  const topDebtors = [...summary].sort((a, b) => (b.total_due || 0) - (a.total_due || 0)).slice(0, 5).filter(r => r.total_due > 0)

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Page Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Party Ledger</h1>
        <p className="text-xs text-gray-500">Track dues and payment history — customers & vendors</p>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6 bg-white p-1 rounded-lg">
          <TabsTrigger value="dashboard" className="flex items-center gap-2 rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="ledger" className="flex items-center gap-2 rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white">
            <BookOpen className="w-4 h-4" />
            Ledger
          </TabsTrigger>
        </TabsList>

        {/* ── TAB 1: DASHBOARD ── */}
        <TabsContent value="dashboard" className="mt-4 space-y-6">
          {loading ? (
            <div className="py-20 text-center text-gray-400 text-sm">Loading analytics...</div>
          ) : (
            <>
              {/* B2C Section */}
              <div className="space-y-4">
                <div>
                  <h2 className="font-semibold text-gray-800 text-sm">Customer Receivables</h2>
                  <p className="text-xs text-gray-400">Money owed to you by customers</p>
                </div>

                {/* B2C KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { label: "Total Billed", value: `₹${totalBilled.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                    { label: "Collected", value: `₹${totalCollected.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                    { label: "Outstanding", value: `₹${overallOutstanding.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                    { label: "Collection Rate", value: `${collectionRate}%` },
                  ].map(card => (
                    <div key={card.label} className="bg-white rounded-xl border p-4">
                      <p className="text-xs text-gray-500 mb-2">{card.label}</p>
                      <p className="text-xl font-bold text-gray-900">{card.value}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Customer Health */}
                  <div className="bg-white rounded-xl border p-5 space-y-4">
                    <h3 className="font-semibold text-gray-800 text-sm">Customer Health</h3>
                    <div className="space-y-3">
                      {[
                        { label: "Clear / Paid", count: clearCustomers },
                        { label: "With Outstanding", count: stats.customers_with_due },
                        { label: "Overdue", count: overdueCustomers },
                      ].map(item => (
                        <div key={item.label} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{item.label}</span>
                          <span className="font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t pt-3 grid grid-cols-2 gap-3">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">{stats.total_customers}</p>
                        <p className="text-xs text-gray-500">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">{summary.filter(r => r.total_transactions > 0).length}</p>
                        <p className="text-xs text-gray-500">Active</p>
                      </div>
                    </div>
                  </div>

                  {/* Top Debtors */}
                  <div className="lg:col-span-2 bg-white rounded-xl border p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800 text-sm">Top Outstanding Parties</h3>
                      <span className="text-xs text-gray-400">Highest dues</span>
                    </div>
                    {topDebtors.length === 0 ? (
                      <div className="py-8 text-center text-gray-400 text-sm">All parties are clear!</div>
                    ) : (
                      <div className="space-y-3">
                        {topDebtors.map((row, idx) => {
                          const status = getCustomerStatus(row)
                          const StatusIcon = status.icon
                          return (
                            <div key={row._id} className="flex items-center gap-3 group">
                              <span className="w-5 text-xs text-gray-400">#{idx + 1}</span>
                              <p className="text-sm font-medium text-gray-900 flex-1 truncate">{row.customer_name}</p>
                              <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${RISK_COLORS[status.label]}`}>
                                <StatusIcon className="w-3 h-3" />{status.label}
                              </span>
                              <p className="text-sm font-semibold text-gray-900 shrink-0">
                                ₹{(row.total_due || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                              </p>
                              <Button variant="ghost" size="sm"
                                className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2"
                                onClick={() => navigate(`/dashboard/customer-ledger/${row.customer_id || row._id}`)}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* B2B Section */}
              <div className="space-y-4">
                <div>
                  <h2 className="font-semibold text-gray-800 text-sm">Vendor Payables</h2>
                  <p className="text-xs text-gray-400">Money you owe to suppliers</p>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { label: "Total Purchased", value: vendorsLoading ? "—" : `₹${(vendorSummary.total_billed || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                    { label: "Paid", value: vendorsLoading ? "—" : `₹${(vendorSummary.total_paid || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                    { label: "Outstanding", value: vendorsLoading ? "—" : `₹${(vendorSummary.total_outstanding || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                    { label: "Payment Rate", value: vendorsLoading ? "—" : `${vendorSummary.total_billed > 0 ? Math.round((vendorSummary.total_paid / vendorSummary.total_billed) * 100) : 0}%` },
                  ].map(card => (
                    <div key={card.label} className="bg-white rounded-xl border p-4">
                      <p className="text-xs text-gray-500 mb-2">{card.label}</p>
                      <p className="text-xl font-bold text-gray-900">{card.value}</p>
                    </div>
                  ))}
                </div>

                {/* Top Vendors list */}
                {vendors.length > 0 && (
                  <div className="bg-white rounded-xl border p-5 space-y-3">
                    <h3 className="font-semibold text-gray-800 text-sm">Vendors ({vendors.length})</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {vendors.slice(0, 8).map(v => (
                        <div key={v._id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                          <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{v.name}</p>
                            {v.phone && <p className="text-xs text-gray-400 truncate">{v.phone}</p>}
                          </div>
                        </div>
                      ))}
                      {vendors.length > 8 && (
                        <div className="flex items-center justify-center bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-500">
                          +{vendors.length - 8} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* ── TAB 2: LEDGER ── */}
        <TabsContent value="ledger" className="mt-4 space-y-4">
          {/* Toggle + Add Party */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 bg-white border rounded-lg p-1 w-fit">
            <button
              onClick={() => setViewFilter("b2c")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewFilter === "b2c" ? "bg-[#0263cc] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Customers
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${viewFilter === "b2c" ? "bg-white/20" : "bg-gray-100"}`}>
                {stats.total_customers}
              </span>
            </button>
            <button
              onClick={() => setViewFilter("b2b")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewFilter === "b2b" ? "bg-[#0263cc] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Vendors
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${viewFilter === "b2b" ? "bg-white/20" : "bg-gray-100"}`}>
                {vendors.length}
              </span>
            </button>
            </div>

            <Button
              size="sm"
              className="bg-[#0263cc] hover:bg-[#0263cc]/90 flex items-center gap-1.5"
              onClick={() => setShowAddParty(true)}
            >
              <UserPlus className="w-4 h-4" />
              Add Party
            </Button>
          </div>

          {/* B2C View */}
          {viewFilter === "b2c" && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Total Customers", value: stats.total_customers },
                  { label: "With Outstanding", value: stats.customers_with_due },
                  { label: "Overdue", value: stats.overdue_customers },
                  { label: "Overall Outstanding", value: `₹${overallOutstanding.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                ].map(item => (
                  <div key={item.label} className="bg-white rounded-xl border p-4">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className="text-xl font-bold text-gray-900">{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Search + Refresh */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search by name, phone, or email..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <Button variant="outline" size="icon" onClick={load} title="Refresh">
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
              </div>

              {/* B2C Table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                {loading ? (
                  <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
                ) : filtered.length === 0 ? (
                  <div className="py-16 text-center text-gray-400 text-sm">
                    {search ? "No customers match your search." : "No parties yet. Add a B2C party to get started."}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Party</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Billed</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Paid</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Due</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Txns</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filtered.map((row) => {
                          const status = getCustomerStatus(row)
                          const StatusIcon = status.icon
                          const hasT = row.total_transactions > 0
                          return (
                            <tr key={row._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-gray-900">{row.customer_name}</p>
                                  {row.customer_phone && <p className="text-xs text-gray-400">{row.customer_phone}</p>}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right text-gray-700 font-medium">
                                {hasT ? `₹${(row.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                              </td>
                              <td className="px-4 py-3 text-right text-green-600 font-medium">
                                {hasT ? `₹${(row.total_paid || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-red-600">
                                {row.total_due > 0 ? `₹${(row.total_due || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                              </td>
                              <td className="px-4 py-3 text-center text-gray-600">{row.total_transactions || 0}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${RISK_COLORS[status.label]}`}>
                                  <StatusIcon className="w-3 h-3" />{status.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-gray-400 hover:text-gray-600"
                                    onClick={() => setEditingParty({ _id: row.customer_id || row._id, name: row.customer_name, phone: row.customer_phone || "", email: row.customer_email || "" })}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                    onClick={() => navigate(`/dashboard/customer-ledger/${row.customer_id || row._id}`)}>
                                    <Eye className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">View</span>
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* B2B View */}
          {viewFilter === "b2b" && (
            <>
              {/* B2B Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Total Vendors", value: vendorsLoading ? "—" : vendors.length },
                  { label: "With Outstanding", value: vendorsLoading ? "—" : vendors.filter(v => (v.transactions || []).some(t => t.status !== "paid")).length },
                  { label: "Overdue", value: vendorsLoading ? "—" : vendors.filter(v => (v.transactions || []).some(t => t.status === "overdue")).length },
                  {
                    label: "Overall Outstanding",
                    value: vendorsLoading ? "—" : `₹${vendors.reduce((sum, v) => sum + (v.transactions || []).filter(t => t.status !== "paid").reduce((s, t) => s + (parseFloat(t.amount) || 0), 0), 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
                  },
                ].map(item => (
                  <div key={item.label} className="bg-white rounded-xl border p-4">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className="text-xl font-bold text-gray-900">{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search vendors..." className="pl-9" value={vendorSearch} onChange={(e) => setVendorSearch(e.target.value)} />
                </div>
                <Button variant="outline" size="icon" onClick={loadVendors} title="Refresh">
                  <RefreshCw className={`w-4 h-4 ${vendorsLoading ? "animate-spin" : ""}`} />
                </Button>
              </div>

              {/* B2B Table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                {vendorsLoading ? (
                  <div className="py-16 text-center text-gray-400 text-sm">Loading vendors...</div>
                ) : filteredVendors.length === 0 ? (
                  <div className="py-16 text-center text-gray-400 text-sm">
                    {vendorSearch ? "No vendors match your search." : "No vendors yet. Add a B2B vendor."}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredVendors.map((v) => (
                          <tr key={v._id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{v.name}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{v.contactDetails?.phone || v.phone || "—"}</td>
                            <td className="px-4 py-3 text-gray-600">{v.contactDetails?.email || v.email || "—"}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                Supplier
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button variant="ghost" size="sm" className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
                                onClick={() => navigate(`/dashboard/customer-ledger/vendor/${v.id || v._id}`)}>
                                <Eye className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">View</span>
                                <ChevronRight className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

      </Tabs>

      {/* Add Party Dialog */}
      <Dialog open={showAddParty} onOpenChange={(open) => { if (!open) { setShowAddParty(false); setNewParty({ name: "", phone: "", email: "" }) } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Party</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {[
              { label: "Party Name", key: "name", icon: User, required: true, placeholder: "Enter name" },
              { label: "Phone Number", key: "phone", icon: Phone, placeholder: "Enter phone number" },
              { label: "Email Address", key: "email", icon: Mail, placeholder: "Enter email address", type: "email" },
            ].map(field => {
              const Icon = field.icon
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </Label>
                  <div className="relative">
                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={field.type || "text"}
                      placeholder={field.placeholder}
                      className="pl-9"
                      value={newParty[field.key]}
                      onChange={(e) => setNewParty({ ...newParty, [field.key]: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && handleAddParty()}
                    />
                  </div>
                </div>
              )
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddParty(false); setNewParty({ name: "", phone: "", email: "" }) }}>Cancel</Button>
            <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90" onClick={handleAddParty} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {saving ? "Adding..." : "Add Party"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Party Dialog */}
      <Dialog open={!!editingParty} onOpenChange={(open) => { if (!open) setEditingParty(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Party</DialogTitle>
          </DialogHeader>
          {editingParty && (
            <div className="space-y-4">
              {[
                { label: "Name", key: "name", icon: User, required: true, placeholder: "Name" },
                { label: "Phone", key: "phone", icon: Phone, placeholder: "Phone number" },
                { label: "Email", key: "email", icon: Mail, placeholder: "Email address", type: "email" },
              ].map(field => {
                const Icon = field.icon
                return (
                  <div key={field.key} className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="relative">
                      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type={field.type || "text"}
                        placeholder={field.placeholder}
                        className="pl-9"
                        value={editingParty[field.key] || ""}
                        onChange={(e) => setEditingParty({ ...editingParty, [field.key]: e.target.value })}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingParty(null)}>Cancel</Button>
            <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90" onClick={handleEditParty} disabled={editPartyLoading}>
              {editPartyLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
